import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axiosInstance from '../utils/axiosInstance';
import usePageTitle from '../hooks/usePageTitle';
import './PipelineDetail.css';

const PipelineDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState(null);
  usePageTitle(pipeline ? pipeline.name : 'Pipeline Details');
  const [runs, setRuns] = useState([]);
  const [availableFlows, setAvailableFlows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddFlow, setShowAddFlow] = useState(false);
  const [selectedFlowId, setSelectedFlowId] = useState('');
  const [selectedLayer, setSelectedLayer] = useState('bronze');
  const [isRunning, setIsRunning] = useState(false);

  useEffect(() => {
    fetchPipeline();
    fetchRuns();
    fetchAvailableFlows();
  }, [id]);

  const fetchPipeline = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/etl/pipelines/${id}/`);
      setPipeline(response.data);
    } catch (err) {
      console.error('Failed to fetch pipeline:', err);
      setError('Failed to load pipeline');
    } finally {
      setLoading(false);
    }
  };

  const fetchRuns = async () => {
    try {
      const response = await axiosInstance.get(`/etl/pipelines/${id}/runs/`);
      setRuns(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch runs:', err);
    }
  };

  const fetchAvailableFlows = async () => {
    try {
      const response = await axiosInstance.get('/etl/flows/');
      setAvailableFlows(response.data.results || response.data);
    } catch (err) {
      console.error('Failed to fetch flows:', err);
    }
  };

  const handleAddStep = async () => {
    if (!selectedFlowId) return;
    try {
      await axiosInstance.post(`/etl/pipelines/${id}/add_step/`, {
        flow_id: parseInt(selectedFlowId),
        layer: selectedLayer,
      });
      setShowAddFlow(false);
      setSelectedFlowId('');
      fetchPipeline();
    } catch (err) {
      console.error('Failed to add step:', err);
      alert(err.response?.data?.error || 'Failed to add step');
    }
  };

  const handleRemoveStep = async (stepId) => {
    if (!confirm('Remove this step from the pipeline?')) return;
    try {
      await axiosInstance.post(`/etl/pipelines/${id}/remove_step/`, {
        step_id: stepId,
      });
      fetchPipeline();
    } catch (err) {
      console.error('Failed to remove step:', err);
      alert('Failed to remove step');
    }
  };

  const handleMoveStep = async (stepId, direction) => {
    const step = pipeline.steps.find(s => s.id === stepId);
    if (!step) return;

    const newOrder = direction === 'up' ? step.step_order - 1 : step.step_order + 1;
    if (newOrder < 0) return;

    try {
      await axiosInstance.post(`/etl/pipelines/${id}/reorder_steps/`, {
        step_id: stepId,
        new_order: newOrder,
      });
      fetchPipeline();
    } catch (err) {
      console.error('Failed to reorder step:', err);
    }
  };

  const handleTriggerPipeline = async () => {
    try {
      setIsRunning(true);
      await axiosInstance.post(`/etl/pipelines/${id}/trigger/`);
      fetchPipeline();
      fetchRuns();
    } catch (err) {
      console.error('Failed to trigger pipeline:', err);
      alert('Failed to trigger pipeline');
    } finally {
      setIsRunning(false);
    }
  };

  const getLayerBadgeClass = (layer) => {
    switch (layer) {
      case 'bronze': return 'layer-badge layer-bronze';
      case 'silver': return 'layer-badge layer-silver';
      case 'gold': return 'layer-badge layer-gold';
      default: return 'layer-badge';
    }
  };

  const getFlowTypeBadgeClass = (flowType) => {
    switch (flowType) {
      case 'odoo': return 'flow-type-badge flow-type-odoo';
      case 'sql': return 'flow-type-badge flow-type-sql';
      case 'api': return 'flow-type-badge flow-type-api';
      default: return 'flow-type-badge';
    }
  };

  const getRunStatusBadge = (status) => {
    const statusMap = {
      pending: 'run-status run-pending',
      running: 'run-status run-running',
      completed: 'run-status run-completed',
      partial: 'run-status run-partial',
      failed: 'run-status run-failed',
      cancelled: 'run-status run-cancelled',
    };
    return statusMap[status] || 'run-status';
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString();
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '-';
    if (seconds < 60) return `${seconds}s`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
    return `${Math.floor(seconds / 3600)}h ${Math.floor((seconds % 3600) / 60)}m`;
  };

  // Filter out flows already in the pipeline
  const flowsNotInPipeline = availableFlows.filter(
    flow => !pipeline?.steps?.some(step => step.flow === flow.id)
  );

  if (loading) {
    return (
      <div className="pipeline-detail-container">
        <div className="loading-state">Loading pipeline...</div>
      </div>
    );
  }

  if (error || !pipeline) {
    return (
      <div className="pipeline-detail-container">
        <div className="error-state">{error || 'Pipeline not found'}</div>
      </div>
    );
  }

  return (
    <div className="pipeline-detail-container">
      <div className="pipeline-detail-header">
        <button onClick={() => navigate('/pipelines')} className="btn-back">
          &larr; Back to Pipelines
        </button>
        <div className="header-content">
          <div className="header-title-row">
            <h1>{pipeline.name}</h1>
            <span className={`status-badge status-${pipeline.status}`}>
              {pipeline.status}
            </span>
          </div>
          {pipeline.description && (
            <p className="pipeline-description">{pipeline.description}</p>
          )}
        </div>
        <button
          className="btn-run-pipeline"
          onClick={handleTriggerPipeline}
          disabled={isRunning || pipeline.status !== 'active' || !pipeline.steps?.length}
        >
          {isRunning ? 'Running...' : 'Run Pipeline'}
        </button>
      </div>

      <div className="pipeline-stats-bar">
        <div className="stat-item">
          <span className="stat-value">{pipeline.total_runs}</span>
          <span className="stat-label">Total Runs</span>
        </div>
        <div className="stat-item">
          <span className="stat-value success">{pipeline.successful_runs}</span>
          <span className="stat-label">Successful</span>
        </div>
        <div className="stat-item">
          <span className="stat-value failed">{pipeline.failed_runs}</span>
          <span className="stat-label">Failed</span>
        </div>
        <div className="stat-item">
          <span className="stat-value">{pipeline.steps?.length || 0}</span>
          <span className="stat-label">Steps</span>
        </div>
        <div className="stat-item">
          <span className="stat-label-only">Last Run</span>
          <span className="stat-date">{formatDate(pipeline.last_run_at)}</span>
        </div>
      </div>

      <div className="pipeline-content">
        <div className="steps-section">
          <div className="section-header">
            <h2>Pipeline Steps</h2>
            <button
              className="btn-add-step"
              onClick={() => setShowAddFlow(true)}
            >
              + Add Step
            </button>
          </div>

          {showAddFlow && (
            <div className="add-step-form">
              <select
                value={selectedFlowId}
                onChange={(e) => setSelectedFlowId(e.target.value)}
                className="flow-select"
              >
                <option value="">Select a flow...</option>
                {flowsNotInPipeline.map(flow => (
                  <option key={flow.id} value={flow.id}>
                    {flow.name} ({flow.flow_type || 'odoo'})
                  </option>
                ))}
              </select>
              <select
                value={selectedLayer}
                onChange={(e) => setSelectedLayer(e.target.value)}
                className="layer-select"
              >
                <option value="bronze">Bronze</option>
                <option value="silver">Silver</option>
                <option value="gold">Gold</option>
              </select>
              <button className="btn-confirm-add" onClick={handleAddStep}>
                Add
              </button>
              <button className="btn-cancel-add" onClick={() => setShowAddFlow(false)}>
                Cancel
              </button>
            </div>
          )}

          {pipeline.steps?.length === 0 ? (
            <div className="empty-steps">
              <p>No steps in this pipeline yet. Add flows to build your pipeline.</p>
            </div>
          ) : (
            <div className="steps-list">
              {pipeline.steps?.sort((a, b) => a.step_order - b.step_order).map((step, index) => (
                <div key={step.id} className={`step-card ${!step.is_enabled ? 'step-disabled' : ''}`}>
                  <div className="step-order">{index + 1}</div>
                  <div className="step-content">
                    <div className="step-header">
                      <Link to={`/flows/${step.flow}`} className="step-name">
                        {step.flow_name}
                      </Link>
                      <div className="step-badges">
                        <span className={getLayerBadgeClass(step.layer)}>
                          {step.layer}
                        </span>
                        <span className={getFlowTypeBadgeClass(step.flow_type)}>
                          {step.flow_type || 'odoo'}
                        </span>
                      </div>
                    </div>
                    <div className="step-meta">
                      {step.continue_on_failure && (
                        <span className="continue-flag">Continue on failure</span>
                      )}
                      {!step.is_enabled && (
                        <span className="disabled-flag">Disabled</span>
                      )}
                    </div>
                  </div>
                  <div className="step-actions">
                    <button
                      className="btn-move"
                      onClick={() => handleMoveStep(step.id, 'up')}
                      disabled={index === 0}
                      title="Move up"
                    >
                      &#9650;
                    </button>
                    <button
                      className="btn-move"
                      onClick={() => handleMoveStep(step.id, 'down')}
                      disabled={index === pipeline.steps.length - 1}
                      title="Move down"
                    >
                      &#9660;
                    </button>
                    <button
                      className="btn-remove"
                      onClick={() => handleRemoveStep(step.id)}
                      title="Remove step"
                    >
                      &times;
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="runs-section">
          <div className="section-header">
            <h2>Recent Runs</h2>
          </div>

          {runs.length === 0 ? (
            <div className="empty-runs">
              <p>No runs yet. Trigger the pipeline to see execution history.</p>
            </div>
          ) : (
            <div className="runs-list">
              {runs.slice(0, 10).map(run => (
                <div key={run.id} className="run-card">
                  <div className="run-header">
                    <span className="run-id">{run.run_id}</span>
                    <span className={getRunStatusBadge(run.status)}>
                      {run.status}
                    </span>
                  </div>
                  <div className="run-details">
                    <div className="run-progress">
                      <span className="progress-text">
                        {run.steps_completed}/{run.total_steps} steps
                      </span>
                      {run.steps_failed > 0 && (
                        <span className="failed-text">({run.steps_failed} failed)</span>
                      )}
                    </div>
                    <div className="run-times">
                      <span>Started: {formatDate(run.started_at)}</span>
                      {run.duration_seconds && (
                        <span>Duration: {formatDuration(run.duration_seconds)}</span>
                      )}
                    </div>
                  </div>
                  {run.error_message && (
                    <div className="run-error">{run.error_message}</div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PipelineDetail;
